"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateCohortData = exports.addRequiredCohortDates = void 0;
const dateFns = __importStar(require("date-fns"));
const lodash_1 = __importDefault(require("lodash"));
const date_1 = require("../../date");
const error_1 = require("../../error");
const typeguards_1 = __importDefault(require("../../typeguards"));
const { groupBy } = lodash_1.default;
// "date-fns" has many utility functions that help with date math and transformations
// Many of these functions output "Date" objects, we have to be mindful of their timezone
// "date-fns" defaults to "UTC" timezone so need to utilize when doing post-conversions
const DATE_FNS_DEFAULT_TIMEZONE = 'UTC';
/**
 * Utility cohort function to help prepare the original data to have the two "required"
 * date attributes to act as the base and series inputs for generating vintage metadata.
 *
 * @param input array with date attributes to be used as reference
 * @param dateKeys house the keys of the target date attributes to be used for cohorting
 * @returns new input array with added "RequiredCohortDataType" attributes
 */
const addRequiredCohortDates = (inputs, dateKeys) => {
    const convertDateValue = (input, key) => {
        const value = input[key];
        (0, error_1.finleyAssert)(value, `Failed to find date value [key:${key}] [object:${input}]`);
        if (typeguards_1.default.isFullDateStr(value)) {
            return value;
        }
        if (value instanceof Date) {
            return date_1.datestr.dateToFullDateString(value);
        }
        // Currently only supporting "Date" or "FullDateStr" format values
        // Can revisit and add more supported type inputs if needed
        throw new error_1.FinleyError(`Invalid date value [value:${value}] [key:${key}] [input:${input}]`);
    };
    const { baseDateKey, seriesDateKey } = dateKeys;
    return inputs.map((input) => {
        const cohortBaseDate = convertDateValue(input, baseDateKey);
        const cohortSeriesDate = convertDateValue(input, seriesDateKey);
        return { ...input, cohortBaseDate, cohortSeriesDate };
    });
};
exports.addRequiredCohortDates = addRequiredCohortDates;
const _fetchDateConversionFunctions = (cadence) => {
    switch (cadence) {
        case 'day':
            return {
                dateConversionFn: dateFns.startOfDay,
                dateDifferenceFn: dateFns.differenceInCalendarDays,
            };
        case 'week': {
            return {
                dateConversionFn: dateFns.startOfWeek,
                dateDifferenceFn: dateFns.differenceInCalendarWeeks,
            };
        }
        case 'month': {
            return {
                dateConversionFn: dateFns.startOfMonth,
                dateDifferenceFn: dateFns.differenceInCalendarMonths,
            };
        }
        case 'quarter': {
            return {
                dateConversionFn: dateFns.startOfQuarter,
                dateDifferenceFn: dateFns.differenceInCalendarQuarters,
            };
        }
        case 'year': {
            return {
                dateConversionFn: dateFns.startOfYear,
                dateDifferenceFn: dateFns.differenceInCalendarYears,
            };
        }
        default:
            return (0, error_1.finleyAssertUnreachable)(cadence);
    }
};
/**
 * Utility function to help generate the truncated dates to help group data into cohorts
 * by desired vintage (cadence). This then also adds the "cohortSeriesAge" attribute
 * to illustrate the time difference since the base cohort date
 *
 * @param input adjusted single data that houses the foundational cohort dates
 * @param cohortCadences determines the time window grouping for the cohort
 * @returns updated input that houses the truncated dates and time difference
 */
const _addTruncatedCohortDates = (input, cohortCadences) => {
    const { cohortSeriesDate, cohortBaseDate } = input;
    const seriesDateFn = _fetchDateConversionFunctions(cohortCadences.series);
    const baseDateFn = _fetchDateConversionFunctions(cohortCadences.base);
    // Recall these date results are objects outputted from "date-fns" so UTC TZ
    const truncatedSeriesDateObj = seriesDateFn.dateConversionFn(date_1.datestr.fullDateStrToDate(cohortSeriesDate));
    const truncatedBaseDateObj = baseDateFn.dateConversionFn(date_1.datestr.fullDateStrToDate(cohortBaseDate));
    const cohortSeriesAge = seriesDateFn.dateDifferenceFn(truncatedSeriesDateObj, truncatedBaseDateObj);
    return {
        ...input,
        cohortTruncatedBaseDate: date_1.datestr.dateToFullDateString(truncatedBaseDateObj, DATE_FNS_DEFAULT_TIMEZONE),
        cohortTruncatedSeriesDate: date_1.datestr.dateToFullDateString(truncatedSeriesDateObj, DATE_FNS_DEFAULT_TIMEZONE),
        cohortSeriesAge,
    };
};
// !TODO: @eldarshamukhamedov Move to dashboard
/**
 * This groups input data into the specified cohort (based on the cohort cadence) and
 * generates data with aggregate / weighted average value based on input accumulator function.
 * This is done by doing 2 different groupings: first by "base date" to create initial nested
 * structure, then group by "series age" to create target aggregate inputs that are fed into
 * the provided accumulator function.
 * The unit tests for this function is a good illustration of example inputs and their
 * final generated outputs that have data cohorted (aggregated) together
 *
 * @param inputs array of data with base cohort dates already
 * @param cohortCadences represents the duration denomination driving the cohorts
 * @param dataConversionFn function to manage data conversion during cohort group creation
 * @returns array with consolidated data cohorted based on provided base date
 */
const generateCohortData = (inputs, cohortCadences, cohortAccumulatorFn) => {
    const inputsWithCohortTruncates = inputs.map((input) => _addTruncatedCohortDates(input, cohortCadences));
    const cohortDataOutput = [];
    // Group by "cohortTruncatedBaseDate" first to consolidate cohorts by same denomination.
    // Then group by "cohortSeriesAge" to put into respective series data.
    // This way we can utilize the data conversion function against the grouped series to
    // calculate that respective group's aggregate cohorted data
    const baseCohortDataMap = groupBy(inputsWithCohortTruncates, 'cohortTruncatedBaseDate');
    const baseEntries = Object.entries(baseCohortDataMap);
    for (const [cohortTruncatedBaseDate, baseData] of baseEntries) {
        (0, error_1.finleyAssert)(typeguards_1.default.isFullDateStr(cohortTruncatedBaseDate), `Incurred not fullDateStr [date:${cohortTruncatedBaseDate}]`);
        const seriesCohortDataMap = groupBy(baseData, 'cohortSeriesAge');
        const cohortGeneratedData = Object.values(seriesCohortDataMap).map((seriesData) => cohortAccumulatorFn(seriesData));
        cohortDataOutput.push({ cohortTruncatedBaseDate, cohortGeneratedData });
    }
    return cohortDataOutput;
};
exports.generateCohortData = generateCohortData;
//# sourceMappingURL=cohort.js.map